import { Component } from '@theme/component';

class HenryCursorSparkles extends Component {
  #controller;
  #enabled = matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
  #frame = 0;
  #point;
  #lastPoint;
  #lastTime = 0;
  #nextShape = 0;
  #shapes = [];
  #particles = new Map();
  #needsRaise = true;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #setup() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#shapes = [...this.querySelectorAll('template')].map((template) => template.content.firstElementChild);
    if (typeof this.showPopover === 'function') this.popover = 'manual';
    document.addEventListener('pointermove', this.#move, { signal, passive: true, capture: true });
    // Keep the decoration above newly opened search/cart dialogs without taking focus.
    document.addEventListener('beforetoggle', (event) => {
      if (event.target !== this && event.newState === 'open') this.#needsRaise = true;
    }, { signal, capture: true });
    document.addEventListener('pointerdown', this.#clear, { signal, passive: true });
    document.documentElement.addEventListener('pointerleave', this.#clear, { signal });
    document.addEventListener('visibilitychange', this.#clear, { signal });
    document.addEventListener('scroll', this.#clear, { signal, passive: true, capture: true });
    window.addEventListener('blur', this.#clear, { signal });
    window.addEventListener('resize', this.#clear, { signal, passive: true });
    this.#enabled.addEventListener('change', this.#clear, { signal });
  }

  #cleanup() {
    this.#controller?.abort();
    this.#clear();
  }

  #clear = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#point = this.#lastPoint = undefined;
    this.#lastTime = 0;
    for (const [particle, animation] of this.#particles) {
      animation.cancel();
      particle.remove();
    }
    this.#particles.clear();
    if (typeof this.hidePopover === 'function' && this.matches(':popover-open')) this.hidePopover();
    this.#needsRaise = true;
  };

  /** @param {PointerEvent} event */
  #move = (event) => {
    if (!this.#enabled.matches || event.pointerType !== 'mouse' || event.buttons || document.hidden) return;
    this.#point = { x: event.clientX, y: event.clientY };
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#draw);
  };

  #draw = (time) => {
    this.#frame = 0;
    const point = this.#point;
    if (!point || time - this.#lastTime < 55 || this.#particles.size >= 20) return;
    if (this.#lastPoint && Math.hypot(point.x - this.#lastPoint.x, point.y - this.#lastPoint.y) < 22) return;
    this.#lastPoint = point;
    this.#lastTime = time;

    // No continuous animation loop: work only on actual mouse movement.
    const side = this.#nextShape % 2 ? -1 : 1;
    const x = point.x + side * (12 + Math.random() * 14);
    const y = point.y - 12 + Math.random() * 24;
    const driftX = side * 5;
    const driftY = 8;

    const source = this.#shapes[this.#nextShape % this.#shapes.length];
    if (!source) return;
    if (typeof this.showPopover === 'function' && (this.#needsRaise || !this.matches(':popover-open'))) {
      if (this.matches(':popover-open')) this.hidePopover();
      this.showPopover();
      this.#needsRaise = false;
    }
    this.#nextShape++;
    const particle = source.cloneNode(true);
    this.append(particle);
    const size = Number(particle.getAttribute('width'));
    const position = `translate(${x - size / 2}px, ${y - size / 2}px)`;
    const end = `translate(${x - size / 2 + driftX}px, ${y - size / 2 + driftY}px)`;
    const animation = particle.animate([
      { transform: `${position} scale(.25)`, opacity: 0, offset: 0 },
      { transform: `${position} scale(1)`, opacity: .95, offset: .2 },
      { transform: `${end} scale(.85)`, opacity: .8, offset: .6 },
      { transform: `${end} scale(0)`, opacity: 0, offset: 1 },
    ], { duration: 850, easing: 'ease-out', fill: 'forwards' });
    this.#particles.set(particle, animation);
    animation.onfinish = () => {
      particle.remove();
      this.#particles.delete(particle);
    };
  };

}

if (!customElements.get('henry-cursor-sparkles')) {
  customElements.define('henry-cursor-sparkles', HenryCursorSparkles);
}
